import type {
  BudgetProgress, BudgetScope, Category, Currency, DailyBudget, Forecast, ForecastMode, JournalEntry, MonthlyPlanningSummary,
  AssignedTransaction, PlanAssignment, PlanGroup, PlanGroupView, PlanItem, PlanKind, PlanVsActual, PlanningBudget, PlanningFxRate, Book, BudgetSeries, BudgetRecurrence, MonthlyCompleteness,
} from "../../entities/ledger/types";
import { apiRequest } from "../../shared/api/client";

const encode = encodeURIComponent;

export interface PlanningPeriod { start: string; end: string; key: string }

export function planningMonth(offset = 0): PlanningPeriod {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  return { start: start.toISOString(), end: end.toISOString(), key: `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}` };
}

function periodQuery(period: PlanningPeriod): string {
  return new URLSearchParams({ from: period.start, to: period.end }).toString();
}

export interface PlanningData {
  book: Book;
  period: PlanningPeriod;
  planVsActual: PlanVsActual;
  budgets: BudgetProgress;
  summary: MonthlyPlanningSummary;
  daily: DailyBudget;
  forecast: Forecast | null;
  rates: PlanningFxRate[];
  categories: Category[];
  transactions: JournalEntry[];
}

export async function loadPlanning(bookID: string, period: PlanningPeriod): Promise<PlanningData> {
  const base = `/v1/books/${encode(bookID)}`;
  const query = periodQuery(period);
  const [book, planVsActual, budgets, summary, daily, forecast, rates, categories, transactions] = await Promise.all([
    apiRequest<Book>(base),
    apiRequest<PlanVsActual>(`${base}/planning/plan-vs-actual?${query}`),
    apiRequest<BudgetProgress>(`${base}/planning/budget-progress?${query}`),
    apiRequest<MonthlyPlanningSummary>(`${base}/planning/summary?${query}`),
    apiRequest<DailyBudget>(`${base}/planning/daily-budget?${query}`),
    new Date(period.end).getTime() > Date.now()
      ? apiRequest<Forecast>(`${base}/planning/forecast?until=${encode(period.end)}`)
      : Promise.resolve(null),
    apiRequest<{ rates: PlanningFxRate[] }>(`${base}/planning/fx-rates`),
    apiRequest<{ categories: Category[] }>(`${base}/categories`),
    apiRequest<{ transactions: JournalEntry[] }>(`${base}/transactions?${query}&limit=200`),
  ]);
  return { book, period, planVsActual, budgets, summary, daily, forecast, rates: rates.rates, categories: categories.categories, transactions: transactions.transactions };
}

export interface PlanItemInput {
  kind: PlanKind; name: string; amount: string; currency: Currency; categoryId?: string; dueDate?: string; note?: string; periodStart: string; periodEnd: string;
}

export function createPlanItem(bookID: string, input: PlanItemInput): Promise<{ planItem: PlanItem }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/plan-items`, { method: "POST", body: {
    kind: input.kind, name: input.name, amount: { amount: input.amount, currency: input.currency }, categoryId: input.categoryId || undefined,
    periodStart: input.periodStart, periodEnd: input.periodEnd, dueDate: input.dueDate ? new Date(input.dueDate).toISOString() : undefined, note: input.note || undefined,
  } });
}

export function updatePlanItem(bookID: string, itemID: string, patch: { amount?: { amount: string; currency: Currency }; name?: string; categoryId?: string; clearDueDate?: boolean; dueDate?: string }): Promise<{ planItem: PlanItem }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/plan-items/${encode(itemID)}`, { method: "PATCH", body: patch });
}

export function cancelPlanItem(bookID: string, itemID: string): Promise<{ planItem: PlanItem }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/plan-items/${encode(itemID)}/cancel`, { method: "POST", body: {} });
}

export function restorePlanItem(bookID: string, itemID: string): Promise<{ planItem: PlanItem }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/plan-items/${encode(itemID)}/restore`, { method: "POST", body: {} });
}

// A transaction has at most one planning owner, so this is a PUT on the
// transaction rather than a POST that could create a second link.
export function assignTransaction(
  bookID: string,
  transactionID: string,
  target: { planItemId?: string; planGroupId?: string; replace?: boolean },
): Promise<{ planAssignment: PlanAssignment }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/assignments/${encode(transactionID)}`, { method: "PUT", body: target });
}

// The id an assigned fact is keyed and unassigned by: a journal transaction
// or an account movement.
export function assignedSubjectId(assignment: AssignedTransaction): string {
  return assignment.transactionId ?? assignment.accountMovementId ?? "";
}

export function unassignTransaction(bookID: string, transactionID: string): Promise<{ removed: boolean }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/assignments/${encode(transactionID)}`, { method: "DELETE" });
}

export function listPlanGroups(bookID: string, includeArchived = false): Promise<{ groups: PlanGroupView[] }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/groups?includeArchived=${includeArchived}`);
}

export function createPlanGroup(bookID: string, input: { name: string; note?: string }): Promise<{ planGroup: PlanGroup }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/groups`, { method: "POST", body: input });
}

export function updatePlanGroup(bookID: string, groupID: string, patch: { name?: string; note?: string }): Promise<{ planGroup: PlanGroup }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/groups/${encode(groupID)}`, { method: "PATCH", body: patch });
}

export function archivePlanGroup(bookID: string, groupID: string, archived: boolean): Promise<{ planGroup: PlanGroup }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/groups/${encode(groupID)}/${archived ? "archive" : "restore"}`, { method: "POST", body: {} });
}

// An empty groupId takes the item out of every group.
export function setPlanItemGroup(bookID: string, itemID: string, groupId: string): Promise<{ planItem: PlanItem }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/plan-items/${encode(itemID)}/group`, { method: "POST", body: { groupId } });
}

export interface BudgetInput { name: string; limit: string; currency: Currency; categoryIds: string[]; forecastMode: ForecastMode; scope: BudgetScope; periodStart?: string; periodEnd?: string; startDate?: string; endDate?: string; timeZone?: string; note?: string }

export function createBudget(bookID: string, input: BudgetInput): Promise<{ budget: PlanningBudget }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets`, { method: "POST", body: {
    name: input.name, limit: { amount: input.limit, currency: input.currency }, categoryIds: input.categoryIds, forecastMode: input.forecastMode, scope: input.scope,
    periodStart: input.periodStart, periodEnd: input.periodEnd, startDate: input.startDate, endDate: input.endDate, timeZone: input.timeZone, note: input.note || undefined,
  } });
}

export interface BudgetSeriesInput extends Omit<BudgetInput, "periodStart" | "periodEnd" | "endDate" | "timeZone"> {
  startDate: string;
  recurrence: BudgetRecurrence;
  untilDate?: string;
}

export function createBudgetSeries(bookID: string, input: BudgetSeriesInput): Promise<{ series: BudgetSeries }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budget-series`, { method: "POST", body: {
    name: input.name, limit: { amount: input.limit, currency: input.currency }, categoryIds: input.categoryIds,
    forecastMode: input.forecastMode, scope: input.scope, note: input.note || undefined,
    startDate: input.startDate, recurrence: input.recurrence, untilDate: input.untilDate,
  } });
}

export function stopBudgetSeries(bookID: string, seriesID: string, from: string): Promise<{ series: BudgetSeries }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budget-series/${encode(seriesID)}/stop`, { method: "POST", body: { from } });
}

export function updateBudget(bookID: string, budgetID: string, patch: { limit?: { amount: string; currency: Currency }; forecastMode?: ForecastMode; scope?: BudgetScope; categoryIds?: string[]; name?: string }): Promise<{ budget: PlanningBudget }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}`, { method: "PATCH", body: patch });
}

export function archiveBudget(bookID: string, budgetID: string): Promise<{ budget: PlanningBudget }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}/archive`, { method: "POST", body: {} });
}

export function setPlanningFx(bookID: string, base: Currency, quote: Currency, rate: string, note?: string): Promise<{ rate: PlanningFxRate }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/fx-rates/${base}/${quote}`, { method: "PUT", body: { rate, note: note || undefined } });
}

export function deletePlanningFx(bookID: string, base: Currency, quote: Currency): Promise<{ deleted: boolean }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/fx-rates/${base}/${quote}`, { method: "DELETE" });
}

// A budget ignores one named transaction. The journal entry and its category
// are untouched; only this limit stops counting it.
export function excludeFromBudget(bookID: string, budgetID: string, transactionID: string, note = ""): Promise<{ excluded: boolean }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}/exclusions/${encode(transactionID)}`,
    { method: "PUT", body: { note } });
}

export function includeInBudget(bookID: string, budgetID: string, transactionID: string): Promise<{ excluded: boolean }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}/exclusions/${encode(transactionID)}`,
    { method: "DELETE" });
}

export function includeExplicitlyInBudget(bookID: string, budgetID: string, transactionID: string, note = ""): Promise<{ membership: string }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}/memberships/${encode(transactionID)}`,
    { method: "PUT", body: { note } });
}

export function removeExplicitBudgetInclusion(bookID: string, budgetID: string, transactionID: string): Promise<{ membership: string; removed: boolean }> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/budgets/${encode(budgetID)}/memberships/${encode(transactionID)}`,
    { method: "DELETE" });
}

export function loadMonthlyCompleteness(bookID: string, period: PlanningPeriod): Promise<MonthlyCompleteness> {
  return apiRequest(`/v1/books/${encode(bookID)}/planning/completeness?${periodQuery(period)}`);
}
